/**
 * System > Integrations landing - the home for every integration dashboard.
 * Uses the shared `AdminLandingPage` tile view (same as System > Admin Tools and
 * Data Sync > Sync Tools). Cards are driven by `GET /api/admin/integrations`
 * (not static): Freshdesk is wired and clickable; Skimmer/Lightspeed Sync Errors
 * and Register Sheets are disabled "Coming soon" until the follow-on
 * `integrations-framework-adoption` plan wires them backend-side - no landing
 * change needed (a wired integration's detail route is `.../integrations/{key}`).
 */
import React, { useMemo } from 'react'
import { Box, CircularProgress } from '@mui/material'
import HubIcon from '@mui/icons-material/Hub'
import SyncProblemIcon from '@mui/icons-material/SyncProblem'
import DescriptionIcon from '@mui/icons-material/Description'
import type { SvgIconProps } from '@mui/material'

import AdminPageHeader from '../../../../components/admin/shared/AdminPageHeader'
import { ConfluenceIcon, FreshdeskIcon } from '../../../../components/SystemIcons'
import AdminLandingPage, {
  type AdminLandingCard,
} from '../../../../components/admin/AdminLandingPage'
import { useDataFetching } from '../../../../hooks/useDataFetching'
import {
  integrationsApi,
  IntegrationCard as IntegrationCardData,
} from '../../../../services/admin/integrationsApi'

const ICONS: Record<string, React.ComponentType<SvgIconProps>> = {
  freshdesk: FreshdeskIcon,
  confluence: ConfluenceIcon,
  skimmer_sync_errors: SyncProblemIcon,
  sheet_registers: DescriptionIcon,
}

// Detail routes that actually exist (App.tsx). A card links here only when the
// integration is wired AND its route is registered - so the follow-on plan
// flipping `wired` server-side can't produce a dead link before it adds the route.
const DETAIL_ROUTES: Record<string, string> = {
  freshdesk: '/admin/system/integrations/freshdesk',
  confluence: '/admin/system/integrations/confluence',
}

const toCard = (c: IntegrationCardData): AdminLandingCard => {
  const to = c.wired ? DETAIL_ROUTES[c.key] : undefined
  return {
    icon: ICONS[c.key] ?? HubIcon,
    title: c.label,
    description: c.description,
    to,
    buttonLabel: to ? `${c.label} Integration` : 'Coming soon',
    disabled: !to,
  }
}

const IntegrationsLanding: React.FC = () => {
  const { data, loading, error } = useDataFetching<IntegrationCardData[]>({
    fetchFn: () => integrationsApi.listIntegrations(),
    dependencies: [],
  })

  const cards = useMemo(() => (data ?? []).map(toCard), [data])

  if (loading && !data) {
    return (
      <>
        <AdminPageHeader pageKey="integrations" />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </>
    )
  }

  return (
    <AdminLandingPage
      pageKey="integrations"
      alert={error ? { severity: 'error', content: error } : undefined}
      cards={cards}
    />
  )
}

export default IntegrationsLanding
